import { useMemo } from 'react'

import { useAuthContext } from '@modules/auth/hooks/useAuthContext'
import { getCompanyId } from '@modules/search/components/ResultsSection/util/ResultProfile.util'
import { AnyResultProfile, ProfileType, isDelegate } from '@modules/search/types'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import chatApi from '../api'

const useHasContactedMap = ({
  profileIds,
  rawProfiles = [],
}: {
  profileIds?: { id: number | string; type: ProfileType; companyId?: number }[]
  rawProfiles?: AnyResultProfile[]
}) => {
  const queryClient = useQueryClient()

  const { user } = useAuthContext()

  // creating a map of ids from incoming props
  const checkContactedIdsMap: {
    delegateIds?: number[]
    companyIds?: number[]
  } = useMemo(() => {
    const { companyIds, delegateIds } = rawProfiles.reduce(
      (acc, profile) => {
        acc.companyIds.push(getCompanyId(profile))
        if (isDelegate(profile)) {
          acc.delegateIds.push(profile.id)
          return acc
        }
        return acc
      },
      {
        companyIds:
          (profileIds
            ?.filter(i => i.companyId || (i.id && i.type === ProfileType.company))
            .map(i => Number(i.companyId)) as number[]) ?? [],
        delegateIds: (profileIds?.filter(i => i.id && i.type === ProfileType.delegate).map(i => Number(i.id)) as number[]) ?? [],
      },
    )
    return {
      companyIds: [...Array.from(new Set(companyIds))].filter(id => id && id !== user?.companyId),
      delegateIds: [...Array.from(new Set(delegateIds))].filter(id => id && id !== user?.companyId),
    }
  }, [profileIds, rawProfiles])

  const fetchNotCachedData = async (idsMap: { companyIds?: number[]; delegateIds?: number[] }) => {
    // Check which IDs are already cached
    const cachedCompanyData = idsMap.companyIds?.reduce((acc, id) => {
      const cachedValue = queryClient.getQueryData([chatApi.checkContactsWith.key, id, ProfileType.company]) as boolean
      if (cachedValue !== undefined) {
        acc[id] = cachedValue ?? false
      }
      return acc
    }, {} as Record<string, boolean>)

    const cachedDelegateData = idsMap.delegateIds?.reduce((acc, id) => {
      const cachedValue = queryClient.getQueryData([chatApi.checkContactsWith.key, id, ProfileType.delegate]) as boolean
      if (cachedValue !== undefined) {
        acc[id] = cachedValue ?? false
      }
      return acc
    }, {} as Record<string, boolean>)

    // Identify IDs to fetch from the API
    const fetchCompanyIds = idsMap.companyIds?.filter(id => !Object.keys(cachedCompanyData ?? {}).includes(String(id))) ?? []
    const fetchDelegateIds = idsMap.delegateIds?.filter(id => !Object.keys(cachedDelegateData ?? {}).includes(String(id))) ?? []

    if (fetchCompanyIds.length === 0 && fetchDelegateIds.length === 0) {
      return {
        [ProfileType.company]: cachedCompanyData,
        [ProfileType.delegate]: cachedDelegateData,
      }
    }

    // Fetch non-cached data from the API
    const apiResult = await chatApi.checkContactsWith.fn({
      companyIds: fetchCompanyIds,
      delegateIds: fetchDelegateIds,
    })

    // Merge cached and fetched results
    return {
      [ProfileType.company]: { ...cachedCompanyData, ...(apiResult?.[ProfileType.company] || {}) },
      [ProfileType.delegate]: { ...cachedDelegateData, ...(apiResult?.[ProfileType.delegate] || {}) },
    }
  }

  const { data: contactedMap } = useQuery({
    queryKey: [chatApi.checkContactsWith.key, JSON.stringify(checkContactedIdsMap)],
    queryFn: () => fetchNotCachedData(checkContactedIdsMap),
    enabled: !!checkContactedIdsMap?.companyIds?.length || !!checkContactedIdsMap?.delegateIds?.length,
  })

  return contactedMap
}

export default useHasContactedMap
